/* eComHQ ad touches. Shared by every store; installed as assets/ehq-ad-touches.js by shopify/ad-touches/apply.py.
   Remembers which tagged ad links this browser arrived through (the utm tags only, never who the visitor is) and writes the
   list to the Shopify cart as hidden attributes, so the order records the first ad, the last ad and the path between them.
   It only reads the page address and the cart; it never changes a line item. Limits and removal: shopify/ad-touches/README.md. */
(function () {
  'use strict';
  var KEY = 'ehq_touches', SENT = 'ehq_touches_sent', RECHECK = 'ehq_touches_recheck';
  var MAX = 12, MAX_CHARS = 1800, MAX_COOKIE = 3600, FIELD = 120, DAYS = 90, REPEAT_MS = 30 * 60 * 1000;

  /* ---- The list. Plain functions with no browser in them; shopify/ad-touches/test.mjs runs them in Node. ---- */

  function clean(value) { return String(value == null ? '' : value).replace(/\s+/g, ' ').trim().slice(0, FIELD); }

  // One touch from a landing address, or null when the visit did not come through a tagged link.
  function touchFrom(search, now) {
    var q = new URLSearchParams(search || ''), source = q.get('utm_source'), fb = q.get('fbclid');
    var google = !!(q.get('gclid') || q.get('gbraid') || q.get('wbraid') || (q.get('gad_source') && q.get('gad_campaignid')));
    if (!source && !fb && !google) return null;
    // Preserve explicit tags. Google click markers recover an otherwise untagged paid arrival.
    var googleArrival = google && (!source || /^(google|googleads|google_ads|adwords)$/i.test(source));
    var touch = { t: new Date(now).toISOString().replace(/\.\d+Z$/, 'Z'), s: clean(source || (googleArrival ? 'google' : '')), m: clean(q.get('utm_medium') || (googleArrival ? 'cpc' : '')), c: clean(q.get('utm_campaign') || (googleArrival ? q.get('gad_campaignid') : '')), a: clean(q.get('utm_content')) };
    if (fb) touch.f = 1;
    return touch;
  }

  function same(a, b) { return a.s === b.s && a.m === b.m && a.c === b.c && a.a === b.a && (a.f || 0) === (b.f || 0); }

  // Stored text back to a list. Anything that is not a touch is dropped, so a damaged copy never stops the page.
  function parse(text) {
    var out = [];
    try {
      var raw = JSON.parse(text);
      if (!Array.isArray(raw)) return out;
      raw.forEach(function (r) {
        if (!r || typeof r !== 'object' || typeof r.t !== 'string' || isNaN(Date.parse(r.t))) return;
        var touch = { t: r.t.slice(0, 30), s: clean(r.s), m: clean(r.m), c: clean(r.c), a: clean(r.a) };
        if (r.f) touch.f = 1;
        out.push(touch);
      });
    } catch (e) {}
    return out;
  }

  // localStorage and the cookie hold the same list. Either can be cleared or cut short, so the two are joined, oldest first.
  function merge(a, b) {
    var seen = {}, out = [];
    a.concat(b).forEach(function (touch) { var k = JSON.stringify(touch); if (!seen[k]) { seen[k] = 1; out.push(touch); } });
    return out.sort(function (x, y) { return Date.parse(x.t) - Date.parse(y.t); });
  }

  function chars(list) { return JSON.stringify(list).length; }

  // Too many or too long: the oldest touch after the first goes. The first is never dropped, because it is the first touch.
  function fit(list, limit, size) {
    list = list.slice();
    while (list.length > 1 && (list.length > MAX || size(list) > limit)) list.splice(1, 1);
    return list;
  }

  // Adds a touch. The same link opened again inside 30 minutes is a reload, not a second click; the list comes back unchanged.
  function add(list, touch) {
    var last = list[list.length - 1];
    if (last && same(last, touch) && Math.abs(Date.parse(touch.t) - Date.parse(last.t)) < REPEAT_MS) return list;
    return fit(list.concat([touch]), MAX_CHARS, chars);
  }

  // "campaign | ad set | ad | time", readable on the order in the Shopify admin. A bar inside a name would break the columns.
  function label(touch) { return [touch.c, touch.m, touch.a, touch.t].map(function (v) { return String(v).replace(/\|/g, '/'); }).join(' | '); }

  // The underscore keeps the three attributes out of anything a theme prints for the shopper.
  function attributes(list) { return { _ehq_first_touch: label(list[0]), _ehq_last_touch: label(list[list.length - 1]), _ehq_touches: JSON.stringify(list) }; }

  function hash(text) { var h = 5381, i = text.length; while (i) h = (h * 33) ^ text.charCodeAt(--i); return (h >>> 0).toString(36); }

  if (typeof window === 'undefined') {
    if (typeof module === 'object' && module) module.exports = { touchFrom: touchFrom, parse: parse, merge: merge, fit: fit, add: add, label: label, attributes: attributes, hash: hash, chars: chars, limits: { touches: MAX, chars: MAX_CHARS, cookie: MAX_COOKIE, field: FIELD, days: DAYS, repeatMs: REPEAT_MS } };
    return;
  }

  /* ---- The page. Everything below is inside one try: a failure here must never reach the storefront. ---- */
  try {
    if (window.EHQTouches) return;

    // Reading a storage object can itself throw when the browser blocks it.
    var storage = function (name) { try { return window[name]; } catch (e) { return null; } };
    var local = storage('localStorage'), session = storage('sessionStorage');
    var get = function (store, key) { try { return store ? store.getItem(key) : null; } catch (e) { return null; } };
    var set = function (store, key, value) { try { if (store) { if (value === null) store.removeItem(key); else store.setItem(key, value); } } catch (e) {} };

    var cookie = function () {
      try { var m = document.cookie.match(new RegExp('(?:^|; )' + KEY + '=([^;]*)')); return m ? decodeURIComponent(m[1]) : ''; } catch (e) { return ''; }
    };
    var encoded = function (list) { return encodeURIComponent(JSON.stringify(list)); };
    var save = function (list) {
      set(local, KEY, JSON.stringify(list));
      // A cookie holds about 4,000 bytes after encoding, so its copy may be shorter than the one in localStorage.
      try { document.cookie = KEY + '=' + encoded(fit(list, MAX_COOKIE, function (l) { return encoded(l).length; })) + '; Max-Age=' + DAYS * 86400 + '; Path=/; SameSite=Lax' + (location.protocol === 'https:' ? '; Secure' : ''); } catch (e) {}
    };

    var list = fit(merge(parse(get(local, KEY)), parse(cookie())), MAX_CHARS, chars);
    var touch = touchFrom(location.search, Date.now());
    if (touch) list = add(list, touch);
    if (list.length && (JSON.stringify(list) !== get(local, KEY) || !cookie())) save(list);

    var root = '/';
    try { root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; } catch (e) {}

    // This script's own cart calls go through XMLHttpRequest, so the theme's fetch listeners (the cart drawer's among them) never see them.
    var request = function (method, path, body, done) {
      try {
        var x = new XMLHttpRequest();
        x._ehqTouches = true;
        x.open(method, root + path, true);
        x.setRequestHeader('Accept', 'application/json');
        if (body) x.setRequestHeader('Content-Type', 'application/json');
        x.onloadend = function () { var json = null; try { if (x.status >= 200 && x.status < 300) json = JSON.parse(x.responseText); } catch (e) {} done(json); };
        x.send(body ? JSON.stringify(body) : null);
      } catch (e) { done(null); }
    };

    var busy = false, themeWrites = 0, timer = 0, waits = 0, check;
    var later = function (ms) { clearTimeout(timer); timer = setTimeout(function () { try { check(); } catch (e) {} }, ms); };
    // Two cart changes sent at once can overwrite each other, so this never writes while the theme is changing the cart.
    var clear = function () {
      if (busy || !list.length) return false;
      if (themeWrites > 0) { if (waits++ < 8) later(500); return false; }
      waits = 0;
      return true;
    };

    // Writes the list to the cart. Only attributes are sent, so line items, the note and discounts are left as they are.
    var write = function () {
      if (!clear()) return;
      busy = true;
      var attrs = attributes(list);
      request('POST', 'cart/update.js', { attributes: attrs }, function (cart) {
        busy = false;
        if (cart && cart.attributes && cart.attributes._ehq_touches === attrs._ehq_touches) set(session, SENT, hash(attrs._ehq_touches));
      });
    };

    // After an add the cart can be a new one (the last became an order). Ask what it holds and write only when the list is missing.
    check = function () {
      if (!clear()) return;
      busy = true;
      var wanted = JSON.stringify(list);
      request('GET', 'cart.js', null, function (cart) {
        busy = false;
        if (!cart) return;
        if (cart.attributes && cart.attributes._ehq_touches === wanted) set(session, SENT, hash(wanted)); else write();
      });
    };

    // Once per page load, and only when the list is not the one this browser session last put on the cart.
    if (list.length) {
      if (get(session, SENT) !== hash(JSON.stringify(list))) write();
      else if (get(session, RECHECK)) check();
    }
    set(session, RECHECK, null);

    // Every add to cart, however the theme makes it. The request and its answer are passed through untouched and are never delayed.
    var ADD = /\/cart\/add(\.js)?(\?|$)/, CHANGE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
    var began = function () { themeWrites++; };
    var ended = function (wasAdd) { themeWrites = Math.max(0, themeWrites - 1); if (wasAdd) later(1200); };

    var nativeFetch = window.fetch;
    if (typeof nativeFetch === 'function') window.fetch = function (input) {
      var promise = nativeFetch.apply(window, arguments);
      try {
        var url = typeof input === 'string' ? input : String((input && (input.url || input.href)) || input || '');
        if (CHANGE.test(url)) {
          var wasAdd = ADD.test(url);
          began();
          promise.then(function () { try { ended(wasAdd); } catch (e) {} }, function () { try { ended(false); } catch (e) {} });
        }
      } catch (e) {}
      return promise;
    };

    var nativeOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        if (!this._ehqTouches && CHANGE.test(String(url))) {
          var wasAdd = ADD.test(String(url));
          this.addEventListener('loadstart', began);
          this.addEventListener('loadend', function () { try { ended(wasAdd); } catch (e) {} });
        }
      } catch (e) {}
      return nativeOpen.apply(this, arguments);
    };

    // A form that posts to /cart/add without script reloads the page; the next page asks the cart what it holds.
    document.addEventListener('submit', function (event) {
      try { var form = event.target; if (form && form.getAttribute && /\/cart\/add/.test(form.getAttribute('action') || '')) set(session, RECHECK, '1'); } catch (e) {}
    }, true);

    // Cart events some themes announce. None of the current store themes sends one; a theme that does is covered without a change here.
    ['cart:update', 'cart:updated', 'cart:refresh', 'cart:change', 'cart:added', 'cart:item-added', 'product:added', 'ajaxProduct:added'].forEach(function (name) {
      document.addEventListener(name, function () { try { later(1200); } catch (e) {} });
    });

    window.EHQTouches = { list: function () { return list.slice(); }, attributes: function () { return list.length ? attributes(list) : null; }, sync: function () { later(0); } };
  } catch (e) {}
})();
